import { CloudUpload, PhotoCamera } from '@mui/icons-material';
import {
    Box,
    Button,
    FormControlLabel,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
    alpha,
} from '@mui/material';
import React, { useCallback, useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useAddReviewPhotosMutation } from '../../../../app/Slices/SevenDayApiSlice';
import toast from '../../../shared/Toast';
import { Media } from '../../../types/user';
import { StarRatingField } from '../FormFields';
import PhotoPreviews from '../PhotoPreviews';
import { ReviewFormValues, StepKey, TagsByStep } from '../types';
import { asPath } from '../utils';
import ConditionalTags from './ConditionalTags';
import Summary from './Summary';

interface StepBodyProps {
    currentKey: StepKey;
    onSubmit: () => void;
    tags: TagsByStep;
    visibleKeys: StepKey[];
}

const RATING_STEP_CONFIG = {
    cleanliness: {
        captions: ['Very dirty', 'Dirty', 'Acceptable', 'Clean', 'Spotless'],
        positiveLabel: 'What was clean or tidy?',
        negativeLabel: 'What needed attention?',
    },
    accuracy: {
        captions: [
            'Not at all accurate',
            'Not very accurate',
            'Fairly accurate',
            'Very accurate',
            'Extremely accurate',
        ],
        positiveLabel: 'What matched or exceeded expectations?',
        negativeLabel: 'What was inaccurate?',
    },
    communication: {
        captions: ['Very poor', 'Poor', 'Average', 'Good', 'Excellent'],
        positiveLabel: 'What went well?',
        negativeLabel: 'What was lacking?',
    },
    access: {
        captions: [
            'Very difficult',
            'Difficult',
            'Manageable',
            'Easy',
            'Very easy',
        ],
        positiveLabel: 'What was easy?',
        negativeLabel: 'What made it hard?',
    },
    value: {
        captions: [
            'Poor value',
            'Below average',
            'Fair value',
            'Good value',
            'Excellent value',
        ],
        positiveLabel: 'What added value?',
        negativeLabel: 'What reduced value?',
    },
};

interface PhotoUploadZoneProps {
    photos: Media[] | undefined;
    onPhotosChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    uploadingPhotos: boolean;
}

const PhotoUploadZone: React.FC<PhotoUploadZoneProps> = ({
    photos,
    onPhotosChange,
    uploadingPhotos,
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);

            const files = Array.from(e.dataTransfer.files).filter((file) =>
                file.type.startsWith('image/'),
            );

            if (files.length > 0) {
                const syntheticEvent = {
                    target: {
                        files: files,
                    },
                } as unknown as React.ChangeEvent<HTMLInputElement>;

                onPhotosChange(syntheticEvent);
            }
        },
        [onPhotosChange],
    );

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const photoCount = Array.isArray(photos) ? photos.length : 0;

    return (
        <Box>
            <Typography variant='subtitle2' sx={{ mb: 2 }}>
                Photos (optional)
            </Typography>

            <Paper
                elevation={0}
                onClick={handleClick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                    border: (theme) =>
                        `2px dashed ${
                            isDragOver
                                ? theme.palette.primary.main
                                : theme.palette.divider
                        }`,
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: uploadingPhotos ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: (theme) =>
                        isDragOver
                            ? alpha(theme.palette.primary.main, 0.04)
                            : 'transparent',
                    '&:hover': {
                        backgroundColor: (theme) =>
                            uploadingPhotos
                                ? 'transparent'
                                : alpha(theme.palette.primary.main, 0.04),
                        borderColor: (theme) =>
                            uploadingPhotos
                                ? theme.palette.divider
                                : theme.palette.primary.main,
                    },
                }}
            >
                <input
                    ref={fileInputRef}
                    type='file'
                    accept='image/*'
                    multiple
                    style={{ display: 'none' }}
                    onChange={onPhotosChange}
                    disabled={uploadingPhotos}
                />

                <Stack spacing={2} alignItems='center'>
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: (theme) =>
                                alpha(theme.palette.primary.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {uploadingPhotos ? (
                            <CloudUpload
                                sx={{
                                    fontSize: 32,
                                    color: 'text.primary',
                                }}
                            />
                        ) : (
                            <PhotoCamera
                                sx={{
                                    fontSize: 32,
                                    color: 'primary.main',
                                }}
                            />
                        )}
                    </Box>

                    <Stack spacing={1} alignItems='center'>
                        <Typography variant='body1' color='text.primary'>
                            {uploadingPhotos
                                ? 'Uploading photos...'
                                : isDragOver
                                  ? 'Drop photos here'
                                  : 'Drag photos here or click to browse'}
                        </Typography>

                        <Typography variant='body3' color='text.primary'>
                            Maximum 15 photos • Landscape orientation preferred
                            • Max 5MB each
                        </Typography>

                        {!uploadingPhotos && (
                            <Button
                                variant='outlined'
                                size='small'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClick();
                                }}
                                sx={{ mt: 1 }}
                            >
                                Choose Files
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Paper>

            {photoCount > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography
                        variant='caption'
                        sx={{
                            display: 'block',
                            mb: 1,
                            color: 'success.main',
                            fontWeight: 500,
                        }}
                    >
                        ✓ {photoCount} photo{photoCount !== 1 ? 's' : ''}{' '}
                        selected
                    </Typography>
                    <PhotoPreviews
                        name={asPath('photos')}
                        disabled={uploadingPhotos}
                    />
                </Box>
            )}
        </Box>
    );
};

const StepBody: React.FC<StepBodyProps> = ({
    currentKey,
    onSubmit,
    tags,
    visibleKeys,
}) => {
    const { control, watch } = useFormContext<ReviewFormValues>();
    const { append } = useFieldArray({ control, name: 'photos' });
    const photos = watch('photos');
    const [addPhotos, { isLoading: uploadingPhotos }] =
        useAddReviewPhotosMutation();

    const renderRatingStep = (stepKey: keyof typeof RATING_STEP_CONFIG) => {
        const config = RATING_STEP_CONFIG[stepKey];
        return (
            <Stack>
                <StarRatingField
                    name={asPath(`${stepKey}.rating`)}
                    captions={config.captions}
                />

                <ConditionalTags
                    step={stepKey}
                    tags={tags}
                    positiveLabel={config.positiveLabel}
                    negativeLabel={config.negativeLabel}
                />
            </Stack>
        );
    };

    const validateImg = async (element: Media): Promise<boolean> => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = function () {
                const height = img.height;
                const width = img.width;
                const portrait = height > width;
                resolve(!portrait);
            };

            img.src = element.src;
        });
    };

    const handlePhotos = async (e: any) => {
        if (e.target.files?.length > 10) {
            toast.error(`Maximum 15 photos can be uploaded`);
        } else if (photos?.length + e.target.files?.length > 10) {
            toast.error(`Maximum 15 photos can be uploaded`);
        } else {
            const formData = new FormData();

            for (const image of e.target.files) {
                formData.append('files', image);
            }

            const requestInfo = formData;

            addPhotos(requestInfo).then((data: any) => {
                if (data) {
                    if (data?.error) {
                        toast.error(`Photo exceeds 5MB size limit`);
                    } else {
                        data.data?.forEach((element: Media) => {
                            validateImg(element).then((valid) => {
                                if (valid) {
                                    append(element);
                                } else {
                                    toast.error(`Photo must be landscape`);
                                }
                            });
                        });
                    }
                } else {
                    toast.error(`Failed to upload photos`);
                }
            });
        }
    };

    switch (currentKey) {
        case 'overall':
            return (
                <Stack spacing={2}>
                    <StarRatingField
                        name={asPath('rating')}
                        captions={['Terrible', 'Bad', 'Ok', 'Good', 'Great']}
                    />
                </Stack>
            );
        case 'cleanliness':
        case 'accuracy':
        case 'communication':
        case 'access':
        case 'value':
            return renderRatingStep(currentKey);
        case 'details':
            return (
                <Stack spacing={2} sx={{ pt: 1, pb: 1 }}>
                    <Controller
                        key={'reviewText'}
                        name={asPath('reviewText')}
                        control={control}
                        render={({ field, fieldState }) => {
                            const currentLength = field.value?.length || 0;
                            const maxLength = 2000;
                            return (
                                <TextField
                                    {...field}
                                    placeholder='Share the highlights of your experience and what made it special'
                                    fullWidth
                                    multiline
                                    minRows={4}
                                    error={!!fieldState.error}
                                    helperText={
                                        fieldState.error?.message ||
                                        `${currentLength}/${maxLength} characters`
                                    }
                                    inputProps={{ maxLength }}
                                />
                            );
                        }}
                    />

                    <Controller
                        key={'photos'}
                        name={asPath('photos')}
                        control={control}
                        render={({ field }) => (
                            <PhotoUploadZone
                                photos={field.value}
                                onPhotosChange={handlePhotos}
                                uploadingPhotos={uploadingPhotos}
                            />
                        )}
                    />

                    <Controller
                        key={'isAnonymous'}
                        name={asPath('isAnonymous')}
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={Boolean(field.value)}
                                        onChange={(event) =>
                                            field.onChange(event.target.checked)
                                        }
                                    />
                                }
                                label='Post this review anonymously'
                            />
                        )}
                    />
                </Stack>
            );
        case 'review':
            return <Summary onSubmit={onSubmit} visibleKeys={visibleKeys} />;
        default:
            return null;
    }
};

export default StepBody;
